import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  checkPublishReady,
  slugify,
  isValidSlug,
  type Farmacia,
} from "@farmacias/schema";
import {
  createFarmacia,
  deleteFarmacia,
  getFarmacia,
  saveFarmacia,
  setStatus,
  slugExists,
  uploadFoto,
  uploadLogo,
} from "../api";
import { FarmaciaForm } from "../components/FarmaciaForm";
import { Preview } from "../components/Preview";
import { CustomDomainCard } from "../components/CustomDomainCard";
import { Text } from "../components/Fields";

export function EditPage({ mode }: { mode: "create" | "edit" }) {
  return mode === "create" ? <CreateFlow /> : <EditFlow />;
}

function CreateFlow() {
  const nav = useNavigate();
  const [nome, setNome] = useState("");
  const [slug, setSlug] = useState("");
  const [touchedSlug, setTouchedSlug] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const effectiveSlug = touchedSlug ? slug : slugify(nome);

  async function submit() {
    setErr(null);
    if (!isValidSlug(effectiveSlug)) {
      setErr("Slug inválido ou reservado (minúsculas, sem acento, 3–63 caracteres).");
      return;
    }
    setBusy(true);
    try {
      if (await slugExists(effectiveSlug)) {
        setErr("Já existe uma farmácia com esse slug.");
        return;
      }
      await createFarmacia(effectiveSlug, { nomeFantasia: nome });
      nav(`/f/${effectiveSlug}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao criar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <h1 className="text-xl font-bold">Nova farmácia</h1>
      <div className="card space-y-4">
        <Text label="Nome fantasia" required value={nome} onChange={setNome} />
        <Text
          label="Slug (subdomínio)"
          required
          value={effectiveSlug}
          onChange={(v) => {
            setTouchedSlug(true);
            setSlug(slugify(v));
          }}
          hint={`${effectiveSlug || "slug"}.sites.oniochat.com.br`}
        />
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button className="btn-primary" disabled={busy || !nome} onClick={() => void submit()}>
            Criar rascunho
          </button>
          <Link to="/" className="btn-ghost">
            Cancelar
          </Link>
        </div>
      </div>
    </div>
  );
}

function EditFlow() {
  const { slug = "" } = useParams();
  const nav = useNavigate();

  const [loaded, setLoaded] = useState<Farmacia | null>(null);
  const [draft, setDraft] = useState<Farmacia | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getFarmacia(slug)
      .then((f) => {
        if (!f) return setNotFound(true);
        setLoaded(f);
        setDraft(f);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Erro ao carregar"));
  }, [slug]);

  const dirty = useMemo(
    () => !!loaded && !!draft && JSON.stringify(stripTimes(loaded)) !== JSON.stringify(stripTimes(draft)),
    [loaded, draft],
  );

  const checklist = useMemo(() => (draft ? checkPublishReady(draft) : null), [draft]);
  const issues = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of checklist?.issues ?? []) if (!m.has(i.field)) m.set(i.field, i.message);
    return m;
  }, [checklist]);

  if (notFound) {
    return (
      <div className="space-y-3">
        <p>Farmácia <code>{slug}</code> não encontrada.</p>
        <Link to="/" className="btn-ghost">Voltar</Link>
      </div>
    );
  }
  if (err && !draft) return <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>;
  if (!draft || !loaded) return <p className="text-sm text-slate-500">Carregando…</p>;

  async function persist(next: Farmacia): Promise<Farmacia> {
    const saved = await saveFarmacia(loaded!, diff(loaded!, next));
    setLoaded(saved);
    setDraft(saved);
    return saved;
  }

  async function onSave() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await persist(draft!);
      setMsg("Salvo.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setBusy(false);
    }
  }

  async function onPublishToggle() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      if (draft!.status === "published") {
        await setStatus(slug, "draft");
        const f = await getFarmacia(slug);
        if (f) {
          setLoaded(f);
          setDraft(f);
        }
        setMsg("Despublicada.");
      } else {
        const saved = dirty ? await persist(draft!) : loaded!;
        const check = checkPublishReady(saved);
        if (!check.ok) {
          setErr("Ainda faltam itens obrigatórios (veja a lista).");
          return;
        }
        await setStatus(slug, "published");
        const f = await getFarmacia(slug);
        if (f) {
          setLoaded(f);
          setDraft(f);
        }
        setMsg("Publicada! Já está no ar.");
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao mudar status");
    } finally {
      setBusy(false);
    }
  }

  async function onUploadLogo(file: File) {
    const logo = await uploadLogo(slug, file);
    await persist({ ...draft!, logo });
  }

  async function onUploadFoto(tipo: "hero" | "fachada", file: File) {
    const enviada = await uploadFoto(slug, tipo, file);
    // Trocar o arquivo quase sempre é trocar o recorte da mesma cena, então o
    // texto alternativo que já existia continua valendo. Sem isto o upload
    // apagava a descrição e gravava vazio, sem aviso e sem desfazer.
    const anterior = tipo === "hero" ? draft!.fotoHero : draft!.fotoFachada;
    const foto = { ...enviada, alt: anterior?.alt ?? "" };
    await persist(tipo === "hero" ? { ...draft!, fotoHero: foto } : { ...draft!, fotoFachada: foto });
  }

  async function onDelete() {
    if (!confirm(`Excluir a farmácia "${draft!.nomeFantasia || slug}"? Não dá pra desfazer.`)) return;
    setBusy(true);
    try {
      await deleteFarmacia(slug);
      nav("/");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao excluir");
      setBusy(false);
    }
  }

  const published = draft.status === "published";

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_minmax(360px,42%)]">
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{draft.nomeFantasia || slug}</h1>
            <a
              className="text-xs text-brand-dark underline"
              href={`https://${slug}.sites.oniochat.com.br`}
              target="_blank"
              rel="noreferrer"
            >
              {slug}.sites.oniochat.com.br
            </a>
          </div>
          <Link to="/" className="btn-ghost !px-3 !py-1 text-xs">← Lista</Link>
        </div>

        <FarmaciaForm
          value={draft}
          onChange={setDraft}
          issues={issues}
          onUploadLogo={onUploadLogo}
          onUploadFoto={onUploadFoto}
        />

        <div className="flex items-center gap-2">
          <button className="btn-danger !px-3 text-xs" disabled={busy} onClick={() => void onDelete()}>
            Excluir
          </button>
        </div>
      </div>

      <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">
              {published ? "🟢 Publicada" : "⚪ Rascunho"}
            </span>
            <span className="text-xs text-slate-400">{dirty ? "alterações não salvas" : "tudo salvo"}</span>
          </div>

          <div className="flex gap-2">
            <button className="btn-primary flex-1" disabled={busy || !dirty} onClick={() => void onSave()}>
              Salvar
            </button>
            <button
              className={published ? "btn-ghost flex-1" : "btn-primary flex-1"}
              disabled={busy || (!published && !checklist?.ok)}
              onClick={() => void onPublishToggle()}
            >
              {published ? "Despublicar" : "Publicar"}
            </button>
          </div>

          {msg && <p className="text-xs text-green-700">{msg}</p>}
          {err && <p className="text-xs text-red-600">{err}</p>}

          {!checklist?.ok && (
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="mb-1 text-xs font-semibold text-slate-600">
                Falta para publicar ({checklist?.issues.length}):
              </p>
              <ul className="list-inside list-disc space-y-0.5 text-xs text-slate-500">
                {checklist?.issues.map((i, idx) => (
                  <li key={idx}>{i.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <CustomDomainCard slug={slug} />

        <Preview farmacia={draft} />
      </div>
    </div>
  );
}

function stripTimes(f: Farmacia) {
  const { createdAt: _c, updatedAt: _u, ...rest } = f;
  return rest;
}

/** Campos que mudaram entre `a` e `b` (shallow) — o que mandar pro saveFarmacia. */
function diff(a: Farmacia, b: Farmacia): Partial<Farmacia> {
  const out: Partial<Farmacia> = {};
  for (const k of Object.keys(b) as (keyof Farmacia)[]) {
    if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) {
      (out as Record<string, unknown>)[k] = b[k];
    }
  }
  return out;
}
